/**
 * `/dye` wire transport — serializes the params and fans out to every tracker
 * base URL, preferring `sendBeacon` (survives navigation) with a
 * keepalive-fetch fallback.
 *
 * This is the only file where "dye" appears as a concept: it names the actual
 * wire endpoint.
 */

import { warn } from '../../util/log';
import { joinUrl, serializeParams } from '../../util/url';
import type { EventParams } from './track';

export function dispatchToTrackers(
  trackerBaseUrls: string[],
  params: EventParams,
  route = '/dye',
): void {
  const query = serializeParams(params);
  for (const base of trackerBaseUrls) {
    sendOne(joinUrl(base, `${route}?${query}`));
  }
}

/**
 * Same fan-out, but the first tracker's response body is returned — `/p` answers
 * with the server-side prediction when `rv` is set. `sendBeacon` cannot read a
 * response, so that one request goes out as a keepalive fetch instead, which
 * still survives navigation. Resolves `null` if the body is unreadable.
 */
export function dispatchExpectingResponse(
  trackerBaseUrls: string[],
  params: EventParams,
  route: string,
): Promise<string | null> {
  const query = serializeParams(params);
  const [first, ...rest] = trackerBaseUrls;
  for (const base of rest) {
    sendOne(joinUrl(base, `${route}?${query}`));
  }
  if (!first || typeof fetch === 'undefined') return Promise.resolve(null);

  return fetch(joinUrl(first, `${route}?${query}`), {
    method: 'GET',
    keepalive: true,
    credentials: 'omit',
  })
    .then((res) => (res.ok ? res.text() : null))
    .catch((err) => {
      warn('analytics fetch failed', err);
      return null;
    });
}

function sendOne(url: string): void {
  if (typeof navigator !== 'undefined' && navigator.sendBeacon) {
    try {
      const beaconUrl = url.includes('?') ? `${url}&beacon=1` : `${url}?beacon=1`;
      if (navigator.sendBeacon(beaconUrl)) return;
    } catch {
      /* fall through */
    }
  }
  if (typeof fetch !== 'undefined') {
    fetch(url, { method: 'GET', keepalive: true, credentials: 'omit' }).catch((err) => {
      warn('analytics fetch failed', err);
    });
  }
}
